import { formatDate } from "@/lib/format";
import { baseUrl } from "@/lib/http";
import { STAGING_STATUS } from "@/types/api";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { stagingsApi } from "./api";
import { stagingsKeys } from "./queryKeys";

interface Props {
  stagingId: number | null;
  onClose: () => void;
}

export function StagingInfoDialog({ stagingId, onClose }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: stagingsKeys.detail(stagingId),
    queryFn: () => stagingsApi.get(stagingId!),
    enabled: stagingId !== null,
  });

  const imgSrc = data?.imageFile?.thumbnailPath280
    ? `${baseUrl}${data.imageFile.thumbnailPath280}`
    : data?.imageFile?.relativePath
    ? `${baseUrl}${data.imageFile.relativePath}`
    : null;

  return (
    <Dialog open={stagingId !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Staging information</DialogTitle>
      <DialogContent>
        {isLoading && <CircularProgress size={24} />}
        {data && (
          <>
            {imgSrc && (
              <Box
                component="img"
                src={imgSrc}
                alt={data.address}
                sx={{ width: "100%", maxHeight: 280, objectFit: "cover", borderRadius: 1, mb: 2 }}
              />
            )}
            <Grid container spacing={2}>
              <Grid size={6}>
                <Typography variant="subtitle2">Address</Typography>
                <Typography>{data.address}</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="subtitle2">Notes</Typography>
                <Typography color="text.secondary">{data.notes || "—"}</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="subtitle2">Status</Typography>
                <Typography>
                  {STAGING_STATUS[data.status as keyof typeof STAGING_STATUS] ?? data.status}
                </Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="subtitle2">Staging Start</Typography>
                <Typography>{formatDate(data.startDate)}</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="subtitle2">Destaging Start</Typography>
                <Typography>{formatDate(data.endDate)}</Typography>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
